import { useState, useEffect, useCallback, useRef } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  X,
  CheckCircle,
  Paperclip,
  Trash,
  ShieldCheck,
  Upload,
  CurrencyInr,
  PaperPlaneTilt,
  IdentificationBadge,
  ClipboardText,
  DownloadSimple,
  WhatsappLogo,
} from "@phosphor-icons/react";
import { fmtDate } from "@/lib/format";

import { useAuth } from "@/context/AuthContext";

const STAGE_ICONS = {
  kyc: IdentificationBadge,
  insurance: ShieldCheck,
  vahan: Upload,
  payment: CurrencyInr,
  docs: PaperPlaneTilt,
};

const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const r = reader.result || "";
      const idx = r.indexOf(",");
      resolve(idx >= 0 ? r.slice(idx + 1) : r);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

function StageCard({ reg, stage, onChanged, isFinance, canEdit }) {
  const regId = reg?.id;
  const fileRef = useRef(null);
  const [notes, setNotes] = useState(stage.notes || "");
  const [amount, setAmount] = useState(stage.amount ?? "");
  const [refNo, setRefNo] = useState(stage.reference_no || "");
  const [thumbNo, setThumbNo] = useState(stage.finance_thumb_no || "");
  const [savingNote, setSavingNote] = useState(false);
  const [savingMoney, setSavingMoney] = useState(false);
  const [savingThumb, setSavingThumb] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [waSend, setWaSend] = useState({ open: false, att: null });
  const Icon = STAGE_ICONS[stage.key] || ClipboardText;
  const isDone = stage.status === "done";
  const isSkipped = stage.status === "skipped";
  // Money capture only on insurance + RTO payment stages (user request).
  const wantsMoney = stage.key === "insurance" || stage.key === "payment";
  const moneyLabel = stage.key === "insurance" ? "Insurance Premium" : "Registration Charges";
  const refLabel = stage.key === "insurance" ? "Policy / Receipt No." : "Challan / Receipt No.";

  const setStageStatus = async (newStatus) => {
    try {
      await api.patch(`/registrations/${regId}/stage/${stage.key}`, { status: newStatus });
      toast.success(`Stage marked ${newStatus}`);
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const saveNote = async () => {
    if ((notes || "") === (stage.notes || "")) return;
    setSavingNote(true);
    try {
      await api.patch(`/registrations/${regId}/stage/${stage.key}`, { notes });
      toast.success("Note saved");
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingNote(false);
    }
  };

  const saveMoney = async () => {
    const amtChanged = String(amount) !== String(stage.amount ?? "");
    const refChanged = (refNo || "") !== (stage.reference_no || "");
    if (!amtChanged && !refChanged) return;
    const num = amount === "" ? null : Number(amount);
    if (amount !== "" && (Number.isNaN(num) || num < 0)) {
      toast.error("Amount must be a positive number");
      return;
    }
    setSavingMoney(true);
    try {
      await api.patch(`/registrations/${regId}/stage/${stage.key}`, {
        amount: num,
        reference_no: refNo,
      });
      toast.success("Saved");
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingMoney(false);
    }
  };

  const saveThumb = async () => {
    if ((thumbNo || "") === (stage.finance_thumb_no || "")) return;
    setSavingThumb(true);
    try {
      await api.patch(`/registrations/${regId}/stage/${stage.key}`, {
        finance_thumb_no: thumbNo,
      });
      toast.success("Finance thumb no. saved");
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingThumb(false);
    }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File too large (max 5 MB)");
      return;
    }
    setUploading(true);
    try {
      const data_b64 = await fileToBase64(file);
      await api.post(`/registrations/${regId}/stage/${stage.key}/attach`, {
        name: file.name,
        mime: file.type || "application/octet-stream",
        data_b64,
      });
      toast.success(`${file.name} attached`);
      if (fileRef.current) fileRef.current.value = "";
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const removeAttachment = async (att) => {
    if (!window.confirm(`Remove ${att.name}?`)) return;
    try {
      await api.delete(`/registrations/${regId}/stage/${stage.key}/attach/${att.id}`);
      toast.success("Removed");
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const downloadAttachment = async (att) => {
    try {
      const { data } = await api.get(
        `/registrations/${regId}/attachment/${stage.key}/${att.id}`
      );
      // Convert base64 → blob → trigger download
      const byteChars = atob(data.data_b64);
      const byteNums = new Array(byteChars.length);
      for (let i = 0; i < byteChars.length; i++) byteNums[i] = byteChars.charCodeAt(i);
      const blob = new Blob([new Uint8Array(byteNums)], { type: data.mime || "application/octet-stream" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = data.name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const isVahan = stage.key === "vahan";

  return (
    <div
      className={`border-2 ${isDone ? "border-[#00B341] bg-[#00B341]/5" : isSkipped ? "border-canvas-line bg-canvas-panel opacity-60" : "border-ink"} p-4 space-y-3`}
      data-testid={`stage-${stage.key}`}
    >
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 border-2 flex items-center justify-center shrink-0 ${isDone ? "bg-[#00B341] border-[#00B341] text-white" : "border-ink text-ink"}`}>
          <Icon size={18} weight="bold" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-display font-semibold">{stage.label}</h3>
            <span className="font-mono text-[10px] uppercase tracking-wider text-ink-muted">
              SLA {stage.sla_days}d
            </span>
            {isDone && (
              <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider text-[#00713a]">
                <CheckCircle size={11} weight="bold" /> done
              </span>
            )}
            {isSkipped && (
              <span className="text-[10px] font-mono uppercase tracking-wider text-ink-muted">skipped</span>
            )}
          </div>
          {isDone && (
            <div className="font-mono text-[10px] text-ink-muted mt-0.5">
              by {stage.completed_by_name || "—"} on {fmtDate(stage.completed_at)}
            </div>
          )}
          {isVahan && isFinance && (
            <div className="mt-2 p-2.5 border border-amber-300 bg-amber-50 space-y-1.5" data-testid={`stage-vahan-finance-thumb-block`}>
              <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-amber-800">
                Vahan Finance Thumb No. <span className="text-destructive">*</span>
              </label>
              <input
                type="text"
                value={thumbNo}
                onChange={(e) => setThumbNo(e.target.value)}
                onBlur={saveThumb}
                readOnly={!canEdit}
                data-testid={`stage-vahan-finance-thumb`}
                className={`w-full px-2 py-1.5 bg-white border border-amber-400 text-xs font-mono focus:outline-none focus:border-amber-600 ${!canEdit ? "cursor-not-allowed opacity-70" : ""}`}
                placeholder="e.g. VF-2026-000123"
              />
              <div className="text-[10px] text-amber-800 font-mono">
                Required for finance cases — appears in the Finance Pending report so Accounts can chase the financier by app #.
                {savingThumb && <span className="ml-2">saving…</span>}
              </div>
            </div>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {!canEdit ? (
            <span
              className="font-mono text-[9px] uppercase tracking-wider px-2 py-1 bg-canvas-panel border border-canvas-line text-ink-subtle"
              title="You don't have permission to edit registrations"
            >
              read-only
            </span>
          ) : (
            <>
              {!isDone && (() => {
                // Money stages refuse to be marked Done without amount + ref.
                // Vahan-on-finance also requires the finance-thumb number.
                // Mirror the backend gate visually so the operator sees the
                // disabled state before clicking.
                const missingMoney =
                  wantsMoney && (Number(amount) <= 0 || !(refNo || "").trim());
                const missingThumb =
                  isVahan && isFinance && !(thumbNo || "").trim();
                const blocked = missingMoney || missingThumb;
                let tooltip = "Mark this stage as completed";
                if (missingMoney) tooltip = `Enter ${moneyLabel} (₹) and ${refLabel} before marking Done`;
                else if (missingThumb) tooltip = "Enter the Vahan Finance Thumb No. before marking Done";
                return (
                  <button
                    type="button"
                    onClick={() => setStageStatus("done")}
                    disabled={blocked}
                    title={tooltip}
                    data-testid={`stage-${stage.key}-mark-done`}
                    className="px-3 py-1.5 bg-[#00B341] text-white text-xs font-semibold hover:bg-[#00713a] disabled:bg-canvas-line disabled:text-ink-muted disabled:cursor-not-allowed disabled:hover:bg-canvas-line"
                  >
                    Mark Done
                  </button>
                );
              })()}
              {isDone && (
                <button
                  type="button"
                  onClick={() => setStageStatus("pending")}
                  className="px-3 py-1.5 border border-canvas-line text-ink-muted text-xs hover:border-ink hover:text-ink"
                >
                  Re-open
                </button>
              )}
              {!isDone && !isSkipped && (
                <button
                  type="button"
                  onClick={() => setStageStatus("skipped")}
                  data-testid={`stage-${stage.key}-skip`}
                  className="px-2 py-1.5 border border-canvas-line text-ink-muted text-xs hover:border-ink hover:text-ink"
                  title="Skip if not applicable"
                >
                  Skip
                </button>
              )}
            </>
          )}
        </div>
      </div>

      <div>
        <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1">
          Notes
        </label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          onBlur={saveNote}
          rows={2}
          readOnly={!canEdit}
          data-testid={`stage-${stage.key}-notes`}
          className={`w-full px-2 py-1.5 bg-canvas border border-canvas-line text-xs focus:outline-none focus:border-ink ${!canEdit ? "cursor-not-allowed opacity-70" : ""}`}
          placeholder="What was done / who to follow up with / reference no."
        />
        {savingNote && <div className="font-mono text-[9px] text-ink-muted mt-0.5">saving…</div>}
      </div>

      {wantsMoney && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1">
              {moneyLabel} (₹)
            </label>
            <input
              type="number"
              inputMode="decimal"
              step="0.01"
              min="0"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              onBlur={saveMoney}
              readOnly={!canEdit}
              data-testid={`stage-${stage.key}-amount`}
              className={`w-full px-2 py-1.5 bg-canvas border border-canvas-line text-xs font-mono tabular-nums focus:outline-none focus:border-ink ${!canEdit ? "cursor-not-allowed opacity-70" : ""}`}
              placeholder="0.00"
            />
          </div>
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1">
              {refLabel}
            </label>
            <input
              type="text"
              value={refNo}
              onChange={(e) => setRefNo(e.target.value)}
              onBlur={saveMoney}
              readOnly={!canEdit}
              data-testid={`stage-${stage.key}-ref`}
              className={`w-full px-2 py-1.5 bg-canvas border border-canvas-line text-xs font-mono focus:outline-none focus:border-ink ${!canEdit ? "cursor-not-allowed opacity-70" : ""}`}
              placeholder="e.g. POL-2024-12345"
            />
          </div>
          {savingMoney && (
            <div className="col-span-full font-mono text-[9px] text-ink-muted">saving…</div>
          )}
        </div>
      )}

      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted inline-flex items-center gap-1">
            <Paperclip size={11} weight="bold" /> Attachments
          </label>
          {canEdit && (
            <input
              ref={fileRef}
              type="file"
              onChange={upload}
              disabled={uploading}
              data-testid={`stage-${stage.key}-file`}
              className="text-[11px]"
            />
          )}
        </div>
        {(stage.attachments || []).length > 0 ? (
          <div className="space-y-1">
            {stage.attachments.map((a) => (
              <div key={a.id} className="flex items-center gap-2 border border-canvas-line px-2 py-1 text-xs">
                <span className="font-mono text-[11px] truncate flex-1">{a.name}</span>
                <span className="font-mono text-[10px] text-ink-muted">{(a.size / 1024).toFixed(0)} KB</span>
                <button
                  type="button"
                  onClick={() => downloadAttachment(a)}
                  className="text-ink-muted hover:text-ink"
                  title="Download"
                >
                  <DownloadSimple size={12} weight="bold" />
                </button>
                {canEdit && (stage.key === "insurance" || stage.key === "vahan" || stage.key === "docs") && (
                  <button
                    type="button"
                    onClick={() => setWaSend({ open: true, att: a })}
                    className="text-ink-muted hover:text-[#00713a]"
                    data-testid={`stage-${stage.key}-att-${a.id}-wa`}
                    title="Send to Customer WhatsApp"
                  >
                    <WhatsappLogo size={14} weight="fill" />
                  </button>
                )}
                {canEdit && (
                  <button
                    type="button"
                    onClick={() => removeAttachment(a)}
                    className="text-ink-muted hover:text-destructive"
                    title="Remove"
                  >
                    <Trash size={12} weight="bold" />
                  </button>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-[11px] text-ink-muted font-mono">No files yet.</div>
        )}
      </div>
      {waSend.open && (
        <WhatsAppSendDialog
          reg={reg}
          stage={stage}
          attachment={waSend.att}
          onClose={() => setWaSend({ open: false, att: null })}
        />
      )}
    </div>
  );
}

export default function RegistrationDetailDrawer({ registrationId, onClose, onChanged, onlyStageKey, onlyPhase }) {
  const { user } = useAuth();
  // Edit is allowed for admin or anyone holding `registrations.edit`.
  // Viewers (Accounts role with only `registrations.view`) get a read-only
  // copy of the drawer — inputs become readOnly, action buttons collapse to
  // a small "read-only" pill.
  const perms = user?.permissions || [];
  const isAdmin = user?.role === "admin";
  // Each team edits only its own stages; the other team's stages are read-only.
  const canEditRto = isAdmin || perms.includes("registrations.manage") || perms.includes("registrations.edit");
  const canEditPost = isAdmin || perms.includes("post_sale.manage") || perms.includes("post_sale.edit");
  const canEdit = canEditRto;
  const [reg, setReg] = useState(null);
  const [loading, setLoading] = useState(false);
  const [notifLog, setNotifLog] = useState([]);
  const [showLog, setShowLog] = useState(false);
  const [testing, setTesting] = useState(false);

  const load = useCallback(async () => {
    if (!registrationId) return;
    setLoading(true);
    try {
      const [{ data }, { data: notifs }] = await Promise.all([
        api.get(`/registrations/${registrationId}`),
        api.get(`/registrations/${registrationId}/notifications`),
      ]);
      setReg(data);
      setNotifLog(notifs || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [registrationId]);

  useEffect(() => { load(); }, [load]);

  const toggleNotify = async () => {
    if (!reg) return;
    try {
      await api.patch(`/registrations/${reg.id}/notify-toggle`, {
        notify_customer: !reg.notify_customer,
      });
      toast.success(reg.notify_customer ? "Customer notifications disabled" : "Customer notifications enabled");
      load();
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const sendTest = async () => {
    if (!reg) return;
    setTesting(true);
    try {
      const { data } = await api.post(`/registrations/${reg.id}/notifications/test`, {
        stage_key: "docs",
      });
      if (data.dry_run) {
        toast.success("Test WhatsApp logged (dry-run — add Twilio creds to send for real)");
      } else if (data.status === "sent") {
        toast.success("Test WhatsApp sent");
      } else {
        toast.warning(`Test result: ${data.status}`);
      }
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setTesting(false);
    }
  };

  if (!registrationId) return null;

  const isComplete = reg?.overall_status === "completed";
  const scoped = (reg?.stages || []).filter((s) => !onlyPhase || (s.phase || "rto") === onlyPhase);
  const doneCount = scoped.filter((s) => s.status === "done").length;
  const totalCount = scoped.length;

  return (
    <div
      className="fixed inset-0 z-40 flex justify-end bg-ink/40"
      onClick={onClose}
      data-testid="reg-detail-drawer"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-canvas-card w-full max-w-2xl h-full overflow-y-auto border-l-2 border-ink"
      >
        <div className="sticky top-0 bg-canvas-card border-b-2 border-ink px-5 py-4 flex items-start gap-3 z-10">
          <div className="flex-1 min-w-0">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted">
              Registration · {reg?.sale_type || (reg?.is_finance ? "FINANCE" : "CASH")}
            </div>
            <h2 className="font-display text-lg font-bold truncate">
              {reg?.customer_name || (loading ? "Loading…" : "—")}
            </h2>
            {reg && (
              <div className="font-mono text-[11px] text-ink-muted">
                {reg.vehicle_model} · {reg.vehicle_vin} · Started {fmtDate(reg.started_at)}
              </div>
            )}
          </div>
          {isComplete && (
            <span className="inline-flex items-center gap-1 px-2 py-1 border border-[#00B341] bg-[#00B341]/10 text-[#00713a] text-xs font-mono uppercase">
              <CheckCircle size={12} weight="bold" /> Completed
            </span>
          )}
          <button
            onClick={onClose}
            className="text-ink-muted hover:text-ink p-1"
            data-testid="reg-drawer-close"
          >
            <X size={20} weight="bold" />
          </button>
        </div>

        {reg && (
          <div className="px-5 py-4 space-y-4">
            {!canEdit && (
              <div
                className="border border-canvas-line bg-canvas-panel px-3 py-2 text-[11px] font-mono uppercase tracking-wider text-ink-muted flex items-center gap-2"
                data-testid="reg-readonly-banner"
              >
                <span className="inline-flex w-2 h-2 rounded-full bg-ink-muted" />
                Read-only · viewer access (no <code>registrations.manage</code> permission)
              </div>
            )}
            {!onlyStageKey && (
              <div className="border border-canvas-line bg-canvas-panel px-3 py-2 flex items-center justify-between text-xs">
                <span className="font-mono text-[10px] uppercase tracking-wider text-ink-muted">Progress</span>
                <span className="font-mono tabular-nums">
                  <b>{doneCount}</b> / {totalCount} stages done
                </span>
              </div>
            )}

            {/* Customer & Vehicle details — surfaces every field required by
                insurance + RTO forms in one read-only block. Operators copy
                from here when filling out government paperwork. */}
            <details className="border-2 border-ink bg-canvas-panel" open data-testid="reg-customer-details">
              <summary className="px-3 py-2 cursor-pointer font-mono text-[10px] uppercase tracking-[0.3em] text-ink-muted hover:bg-canvas-card">
                Customer & Vehicle Details
              </summary>
              <div className="px-3 py-3 border-t border-canvas-line">
                <table className="w-full text-[12px]">
                  <tbody>
                    {(() => {
                      const c = reg.customer || {};
                      const fullName = c.name || [c.first_name, c.last_name].filter(Boolean).join(" ");
                      const relLabel = ({ s_o: "S/o", w_o: "W/o", d_o: "D/o", represented_by: "Represented By" }[c.relation_type] || "S/o · W/o · Represented By");
                      const fullAddress = c.address || [c.house_no, c.street, c.town_village, c.mandal, c.district, c.state, c.pin_code].filter(Boolean).join(", ");
                      return [
                        ["Branch Name", reg.branch_name],
                        ["Sale Type", reg.sale_type],
                        ["VIN", reg.vehicle?.vin],
                        ["Vehicle", [reg.vehicle?.package_name, reg.vehicle?.color].filter(Boolean).join(" · ")],
                        ["Customer Name", fullName],
                        [relLabel, c.relation_name],
                        ["Gender", c.gender ? c.gender[0].toUpperCase() + c.gender.slice(1) : ""],
                        ["Mobile", c.phone],
                        ["Date of Birth", c.dob],
                        ["Address", fullAddress],
                        ["Aadhar No", c.aadhar_no],
                        ["PAN No", c.pan_no],
                        ["Driving Licence No", c.driving_licence_no],
                        ["Nominee", c.nominee_name],
                        ["Relation", (c.nominee_relation || "").toUpperCase()],
                        ["Nominee Age", c.nominee_age],
                      ].map(([label, value]) => (
                        <tr key={label} className="border-b border-canvas-line last:border-b-0">
                          <td className="py-1 pr-3 align-top font-mono text-[10px] uppercase tracking-wider text-ink-muted whitespace-nowrap w-[180px]">{label}</td>
                          <td className="py-1 text-ink">
                            {value || <span className="italic text-ink-subtle">—</span>}
                          </td>
                        </tr>
                      ));
                    })()}
                  </tbody>
                </table>
              </div>
            </details>

            {/* WhatsApp notifications block */}
            <div className="border border-canvas-line p-3 space-y-2" data-testid="notify-panel">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted">
                    Customer WhatsApp Notifications
                  </span>
                  <label className="inline-flex items-center gap-1 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={reg.notify_customer !== false}
                      onChange={toggleNotify}
                      disabled={!canEdit}
                      data-testid="notify-toggle"
                    />
                    <span className="text-xs">{reg.notify_customer !== false ? "Enabled" : "Disabled"}</span>
                  </label>
                </div>
                <div className="flex items-center gap-1">
                  {canEdit && (
                    <button
                      onClick={sendTest}
                      disabled={testing || !reg.customer_phone}
                      data-testid="notify-test-btn"
                      className="px-2 py-1 border border-canvas-line text-[10px] font-mono uppercase tracking-wider hover:border-ink disabled:opacity-50"
                      title={!reg.customer_phone ? "Customer has no phone number on file" : "Send a test WhatsApp now"}
                    >
                      {testing ? "Sending…" : "Test Send"}
                    </button>
                  )}
                  <button
                    onClick={() => setShowLog((v) => !v)}
                    className="px-2 py-1 border border-canvas-line text-[10px] font-mono uppercase tracking-wider hover:border-ink"
                  >
                    {showLog ? "Hide" : `Log (${notifLog.length})`}
                  </button>
                </div>
              </div>
              <div className="text-[11px] text-ink-muted">
                Customer phone: <code className="font-mono">{reg.customer_phone || "—"}</code>
                {!reg.customer_phone && <span className="text-destructive ml-2">⚠ no phone — add via Customers page</span>}
              </div>

              {/* Vendor send log — every real send via EasyGoWP writes here with
                  the raw vendor response body. Diagnosis lives here when a
                  message "went through" but the customer didn't receive it. */}
              {(reg.whatsapp_log || []).length > 0 && (
                <div className="border-t border-canvas-line pt-2 max-h-60 overflow-y-auto" data-testid="reg-whatsapp-vendor-log">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-ink-muted mb-2">
                    EasyGoWP Send History — last {Math.min(5, reg.whatsapp_log.length)} of {reg.whatsapp_log.length}
                  </div>
                  <div className="space-y-2">
                    {[...(reg.whatsapp_log || [])].slice(-5).reverse().map((entry, i) => (
                      <div key={i} className={`p-2 border ${entry.ok ? "border-emerald-300 bg-emerald-50" : "border-destructive bg-red-50"}`}>
                        <div className="flex items-center justify-between text-[11px] font-mono">
                          <span>
                            {entry.at ? new Date(entry.at).toLocaleString() : "—"} · {entry.template?.toUpperCase() || "—"} · to {entry.mobile}
                          </span>
                          <span className={`px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-widest ${entry.ok ? "bg-emerald-600 text-white" : "bg-destructive text-white"}`}>
                            {entry.ok ? "vendor OK" : "vendor FAIL"}
                          </span>
                        </div>
                        <div className="text-[10px] text-ink-muted mt-1">
                          by {entry.by || "—"} · stage {entry.stage_key || "—"} · vars {(entry.variables || []).length}
                        </div>
                        {entry.vendor_response && (
                          <details className="mt-1">
                            <summary className="cursor-pointer text-[10px] font-mono text-ink-muted hover:text-ink">
                              vendor response ▸
                            </summary>
                            <pre className="mt-1 p-2 bg-white border border-canvas-line text-[10px] font-mono whitespace-pre-wrap break-all max-h-40 overflow-auto">
                              {entry.vendor_response}
                            </pre>
                          </details>
                        )}
                      </div>
                    ))}
                  </div>
                  <div className="mt-2 text-[10px] text-ink-muted font-mono">
                    Green = EasyGoWP accepted · Red = EasyGoWP rejected. If vendor is OK but customer didn&apos;t receive, the drop is usually Meta-side (template paused, variable format rejected, or 24-h session window closed).
                  </div>
                </div>
              )}
              {showLog && (
                <div className="border-t border-canvas-line pt-2 max-h-48 overflow-y-auto">
                  {notifLog.length === 0 ? (
                    <div className="text-xs text-ink-muted">No messages yet.</div>
                  ) : (
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="text-left">
                          <th className="px-1 py-1 font-mono text-[10px] uppercase text-ink-muted">When</th>
                          <th className="px-1 py-1 font-mono text-[10px] uppercase text-ink-muted">Stage</th>
                          <th className="px-1 py-1 font-mono text-[10px] uppercase text-ink-muted">Status</th>
                          <th className="px-1 py-1 font-mono text-[10px] uppercase text-ink-muted">Mode</th>
                        </tr>
                      </thead>
                      <tbody>
                        {notifLog.map((n) => (
                          <tr key={n.id} className="border-t border-canvas-line" title={n.body}>
                            <td className="px-1 py-1 font-mono text-[10px] text-ink-muted">{fmtDate(n.created_at)}</td>
                            <td className="px-1 py-1 font-mono text-[10px]">{n.stage_key}</td>
                            <td className={`px-1 py-1 font-mono text-[10px] ${n.status === "sent" ? "text-[#00713a]" : n.status === "failed" ? "text-destructive" : "text-ink-muted"}`}>
                              {n.status}
                            </td>
                            <td className="px-1 py-1 font-mono text-[10px] text-ink-muted">{n.dry_run ? "dry-run" : "live"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </div>

            {/* Stages grouped by phase: Registration (rto) vs Post-Sale.
                Phase is stamped at stage-spec time in REGISTRATION_STAGES.
                Defaults to "rto" for legacy stages missing the phase field. */}
            {(() => {
              const visible = (reg.stages || []).filter((s) => (!onlyStageKey || s.key === onlyStageKey) && (!onlyPhase || (s.phase || "rto") === onlyPhase));
              const rtoStages = visible.filter((s) => (s.phase || "rto") === "rto");
              const postStages = visible.filter((s) => s.phase === "post_sale");
              const renderHeader = (label, count) => (
                <div className="border-b-2 border-ink pb-2 mt-2 flex items-baseline justify-between" data-testid={`reg-section-${label.toLowerCase().replace(/\s+/g, "-")}`}>
                  <h3 className="font-display text-base font-semibold uppercase tracking-wide">{label}</h3>
                  <span className="font-mono text-[10px] text-ink-muted">{count} task{count === 1 ? "" : "s"}</span>
                </div>
              );
              return (
                <>
                  {reg.rto_pending_payment && !onlyStageKey && (
                    <div className="border border-amber-400 bg-amber-50 px-3 py-2 text-xs" data-testid="reg-rto-pending-payment">
                      Registration (RTO) tasks will start once accounts approves the sale. Post-sale tasks are already running.
                    </div>
                  )}
                  {rtoStages.length > 0 && (
                    <>
                      {renderHeader("Registration Tasks", rtoStages.length)}
                      {!canEditRto && <p className="text-[11px] text-ink-muted" data-testid="reg-rto-readonly">View only — handled by the Registrations team.</p>}
                      {rtoStages.map((stage) => (
                        <StageCard key={stage.key} reg={reg} stage={stage} isFinance={reg.is_finance} canEdit={canEditRto} onChanged={() => { load(); onChanged?.(); }} />
                      ))}
                    </>
                  )}
                  {postStages.length > 0 && (
                    <>
                      {renderHeader("Post-Sale Tasks", postStages.length)}
                      {!canEditPost && <p className="text-[11px] text-ink-muted" data-testid="reg-post-readonly">View only — handled by the Post-Sale team.</p>}
                      {postStages.map((stage) => (
                        <StageCard key={stage.key} reg={reg} stage={stage} isFinance={reg.is_finance} canEdit={canEditPost} onChanged={() => { load(); onChanged?.(); }} />
                      ))}
                    </>
                  )}
                </>
              );
            })()}
          </div>
        )}
      </div>
    </div>
  );
}


/**
 * WhatsApp Send dialog — renders the approved template variables as a form,
 * pre-filled from registration + vehicle data. Operator reviews + sends.
 * Templates:
 *   • Insurance (4 vars) {{1}}=customer_name {{2}}=make_model {{3}}=chassis_no
 *                        {{4}}=policy_no
 *   • RC        (4 vars) {{1}}=customer_name {{2}}=make_model {{3}}=chassis_no
 *                        {{4}}=engine_no
 */
function WhatsAppSendDialog({ reg, stage, attachment, onClose }) {
  const isInsurance = stage.key === "insurance";
  // Both "vahan" (Vahan portal submission stage — early RC) and "docs" (final
  // RC book delivery stage) use the RC template. Whichever stage the operator
  // attached the RC PDF to, the button now works.
  const template = isInsurance ? "insurance" : "rc";
  const veh = reg?.vehicle || {};
  const cust = reg?.customer || {};
  const [mobile, setMobile] = useState(reg?.customer_phone || cust.phone || "");

  // Pull cross-stage data so operators don't have to retype what's already
  // captured elsewhere on this reg:
  //   • Insurance stage's `reference_no` → policy number
  const stages = reg?.stages || [];
  const insStage = stages.find((s) => s.key === "insurance") || {};

  const [vars, setVars] = useState(() =>
    isInsurance
      ? [
          reg?.customer_name || cust.name || "",
          [veh.make, veh.model_name || veh.model].filter(Boolean).join(" ") || veh.package_name || "",
          veh.chassis_no || veh.vin || "",
          insStage.reference_no || "",  // policy_no — from Insurance stage
        ]
      : [
          reg?.customer_name || cust.name || "",
          [veh.make, veh.model_name || veh.model].filter(Boolean).join(" ") || veh.package_name || "",
          veh.chassis_no || veh.vin || "",
          veh.engine_no || "",
        ]
  );
  const [sending, setSending] = useState(false);
  const labels = isInsurance
    ? ["Customer Name", "Make/Model", "Chassis No", "Policy No"]
    : ["Customer Name", "Make/Model", "Chassis No", "Engine No"];

  const send = async () => {
    if (!attachment) return;
    if (!mobile.trim()) { toast.error("Mobile is required."); return; }
    if (vars.some((v) => !String(v || "").trim())) { toast.error("Fill every field before sending."); return; }
    setSending(true);
    try {
      const { data } = await api.post(`/whatsapp/registrations/${reg.id}/send`, {
        stage_key: stage.key,
        attachment_id: attachment.id,
        template,
        mobile: mobile.trim(),
        variables: vars.map((v) => String(v).trim()),
      });
      toast.success(`WhatsApp sent — ${data.vendor_response?.slice(0, 60) || "queued by vendor"}`);
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-[110] flex items-center justify-center p-4" data-testid="wa-send-dialog">
      <div className="bg-canvas-card border border-canvas-line w-full max-w-lg p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div className="text-sm font-semibold">
            Send {isInsurance ? "Insurance" : "RC"} → WhatsApp
          </div>
          <button onClick={onClose} className="text-ink-muted hover:text-ink" data-testid="wa-send-close">×</button>
        </div>
        <div className="text-[11px] font-mono text-ink-muted">
          File: <span className="text-ink">{attachment?.name}</span>
        </div>
        <div>
          <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1">Mobile *</label>
          <input
            value={mobile}
            onChange={(e) => setMobile(e.target.value)}
            data-testid="wa-send-mobile"
            className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm font-mono"
            placeholder="10-digit number"
          />
        </div>
        {labels.map((lbl, i) => (
          <div key={i}>
            <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1">
              {`{{${i + 1}}} — ${lbl} *`}
            </label>
            <input
              value={vars[i]}
              onChange={(e) => setVars((prev) => prev.map((v, j) => (j === i ? e.target.value : v)))}
              data-testid={`wa-send-var-${i + 1}`}
              className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm"
            />
          </div>
        ))}
        <div className="flex items-center justify-end gap-2 pt-2 border-t border-canvas-line">
          <button onClick={onClose} className="px-3 py-1.5 border border-canvas-line text-xs font-mono uppercase">
            Cancel
          </button>
          <button
            onClick={send}
            disabled={sending}
            data-testid="wa-send-submit"
            className="px-3 py-1.5 bg-[#00713a] text-white text-xs font-mono uppercase disabled:opacity-50"
          >
            {sending ? "Sending…" : "Send WhatsApp"}
          </button>
        </div>
      </div>
    </div>
  );
}
